/**
 * CallKeypad — in-call dial pad shown in place of the live transcript.
 *
 * Client-only: pressed digits are collected into a display and each press plays
 * a short DTMF-style tone. Nothing is sent to the call or the lead.
 */
import { useEffect, useRef, useState } from "react";
import { Delete, Grid3x3 } from "lucide-react";
import { cn } from "@/lib/utils";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

// Standard DTMF pairs: [row frequency, column frequency] in Hz.
const DTMF_TONES: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};

const TONE_MS = 120;

const CallKeypad = () => {
  const [digits, setDigits] = useState("");
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Release the audio context with the pad so it doesn't linger for the rest of the call.
  useEffect(() => () => {
    audioCtxRef.current?.close().catch(() => { /* noop */ });
    audioCtxRef.current = null;
  }, []);

  // Best effort: a blocked or unsupported AudioContext must never break the call UI.
  const playTone = (key: string) => {
    try {
      const AudioCtx = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = audioCtxRef.current ?? (audioCtxRef.current = new AudioCtx());
      if (ctx.state === "suspended") void ctx.resume();

      const gain = ctx.createGain();
      gain.gain.value = 0.08;
      gain.connect(ctx.destination);
      const end = ctx.currentTime + TONE_MS / 1000;
      DTMF_TONES[key].forEach((freq) => {
        const osc = ctx.createOscillator();
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start();
        osc.stop(end);
      });
    } catch { /* noop */ }
  };

  const press = (key: string) => {
    setDigits((prev) => prev + key);
    playTone(key);
  };

  return (
    <div className="relative mx-5 mb-4 rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="mb-2 flex items-center gap-1.5">
        <Grid3x3 className="w-3 h-3 text-white/50" />
        <span className="text-[10px] font-semibold tracking-wider text-white/50">KEYPAD</span>
      </div>

      <div className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
        <span
          aria-live="polite"
          className={cn(
            "min-w-0 flex-1 truncate text-base font-medium tracking-widest",
            digits ? "text-white" : "text-white/30",
          )}
        >
          {digits || "–"}
        </span>
        <button
          type="button"
          aria-label="Backspace"
          onClick={() => setDigits((prev) => prev.slice(0, -1))}
          disabled={!digits}
          className="text-white/60 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Delete className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => press(key)}
            className="rounded-lg border border-white/10 bg-white/5 py-2.5 text-base font-semibold text-white hover:bg-white/10 active:bg-white/15 transition-colors"
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CallKeypad;
